import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { defineMessages, FormattedMessage, injectIntl, intlShape } from 'react-intl';

const messages = defineMessages({
    boardManagerTitle: {
        id: 'gui.boardManager.title',
        defaultMessage: 'Board Manager',
        description: 'Title for the board manager tab'
    },
    boardManagerDesc: {
        id: 'gui.boardManager.description',
        defaultMessage: 'Search and install official Arduino boards.',
        description: 'Description for the board manager tab'
    },
    searchPlaceholder: {
        id: 'gui.boardManager.searchPlaceholder',
        defaultMessage: 'Search Arduino Boards (e.g. esp32)',
        description: 'Placeholder for board search input'
    },
    searchBtn: {
        id: 'gui.boardManager.search',
        defaultMessage: 'Search',
        description: 'Search button text'
    },
    searching: {
        id: 'gui.boardManager.searching',
        defaultMessage: 'Searching...',
        description: 'Text when search is in progress'
    },
    advancedOptionsShow: {
        id: 'gui.boardManager.advancedOptionsShow',
        defaultMessage: '▶ Advanced Options (Custom Install Path)',
        description: 'Text to show advanced options'
    },
    advancedOptionsHide: {
        id: 'gui.boardManager.advancedOptionsHide',
        defaultMessage: '▼ Hide Advanced Options',
        description: 'Text to hide advanced options'
    },
    recommended: {
        id: 'gui.boardManager.recommended',
        defaultMessage: 'Recommended',
        description: 'Recommended tab title'
    },
    installed: {
        id: 'gui.boardManager.installed',
        defaultMessage: 'Installed ({count})',
        description: 'Installed tab title'
    },
    searchResults: {
        id: 'gui.boardManager.searchResults',
        defaultMessage: 'Search Results {count}',
        description: 'Search results tab title'
    },
    refresh: {
        id: 'gui.boardManager.refresh',
        defaultMessage: '↻ Refresh',
        description: 'Refresh button text'
    },
    refreshing: {
        id: 'gui.boardManager.refreshing',
        defaultMessage: '↻ Refreshing...',
        description: 'Text when refreshing'
    }
});

import styles from './board-manager-tab.css';

const RECOMMENDED_BOARDS = [
    "arduino:avr", "esp32:esp32", "esp8266:esp8266", "arduino:megaavr","renesas_uno","Raspberry Pi Pico/RP2040/RP2350" 
];

class BoardManagerTab extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            searchQuery: '',
            installPath: '',
            showAdvanced: false,
            boards: [],
            installedBoards: new Set(),
            installedBoardsData: [],
            loading: false,
            refreshing: false,
            fetchError: false,
            installingId: null,
            installProgress: '',
            installPercentage: 0,
            rawLogs: '',
            notificationMessage: null,
            notificationType: 'info',
            activeTab: 'recommended'
        };
        this.handleSearch = this.handleSearch.bind(this);
        this.handleInstall = this.handleInstall.bind(this);
        this.handleUninstall = this.handleUninstall.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handlePathChange = this.handlePathChange.bind(this);
        this.toggleAdvanced = this.toggleAdvanced.bind(this);
        this.fetchInstalledboards = this.fetchInstalledboards.bind(this);
        this.handleRecommendedClick = this.handleRecommendedClick.bind(this);
        this.renderBoardCard = this.renderBoardCard.bind(this);
        this.renderSkeletonCard = this.renderSkeletonCard.bind(this);
        this.switchTab = this.switchTab.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handlePathBlur = this.handlePathBlur.bind(this);
        this.handlePickDirectory = this.handlePickDirectory.bind(this);
        this.showNotification = this.showNotification.bind(this);
    }

    showNotification(message, type = 'info', autoClear = true) {
        this.setState({ notificationMessage: message, notificationType: type });
        if (this.notificationTimeout) {
            clearTimeout(this.notificationTimeout);
        }
        if (autoClear) {
            this.notificationTimeout = setTimeout(() => {
                this.setState({ notificationMessage: null });
            }, 4000);
        }
    }

    switchTab (tabName) {
        this.setState({activeTab: tabName});
    }

    handleRefresh () {
        this.fetchInstalledboards();
        if (this.state.activeTab === 'searched' && this.state.searchQuery) {
            this.handleSearch({ preventDefault: () => {} });
        }
    }

    handleRecommendedClick (packageName) {
        this.setState({searchQuery: packageName}, () => {
            this.handleSearch({ preventDefault: () => {} });
        });
    }

    handlePathBlur () {
        this.fetchInstalledboards();
        if (this.state.activeTab === 'searched' && this.state.searchQuery) {
            this.handleSearch({ preventDefault: () => {} });
        }
    }

    async handlePickDirectory () {
        try {
            const response = await fetch('http://localhost:8605/api/boards/pick-directory');
            const json = await response.json();
            if (json.success && json.path) {
                this.setState({installPath: json.path}, () => {
                    this.handlePathBlur();
                });
            }
        } catch (e) {
            console.error('Failed to pick directory:', e);
            if (e instanceof TypeError || (e.message && e.message.includes('Failed to fetch'))) {
                this.showNotification('Failed to fetch. Make sure agent is running in tray.', 'error');
            } else {
                this.showNotification('Failed to open directory picker', 'error');
            }
        }
    }

    toggleAdvanced () {
        this.setState(prevState => ({showAdvanced: !prevState.showAdvanced}));
    }

    componentDidMount () {
        this.fetchInstalledboards();
    }

    async fetchInstalledboards () {
        this.setState({refreshing: true, fetchError: false});
        try {
            const url = `http://localhost:8605/api/boards/list${this.state.installPath ? `?installPath=${encodeURIComponent(this.state.installPath)}` : ''}`;
            const response = await fetch(url);
            const json = await response.json();
            if (json.success && json.data) {
                const installedSet = new Set();
                const boards = Array.isArray(json.data) ? json.data : (json.data.platforms || []);
                const normalizedBoards = [];
                boards.forEach(board => {
                    installedSet.add(board.id);
                    let name = board.id;
                    let architecture = '';
                    if (board.releases && Object.keys(board.releases).length > 0) {
                        const versions = Object.keys(board.releases).sort((a,b) => b.localeCompare(a, undefined, {numeric: true}));
                        const release = board.releases[versions[0]];
                        name = release.name || board.id;
                        architecture = release.architecture || '';
                    }
                    normalizedBoards.push({
                        id: board.id,
                        name: name,
                        author: board.maintainer,
                        website: board.website,
                        architecture: architecture,
                        version: board.installed_version || board.latest_version || ''
                    });
                });
                
                this.setState({
                    installedBoards: installedSet,
                    installedBoardsData: normalizedBoards
                });
            }
        } catch (error) {
            console.error('Failed to fetch installed boards:', error);
            if (error instanceof TypeError || (error.message && error.message.includes('Failed to fetch'))) {
                this.setState({fetchError: true});
                this.showNotification('Failed to fetch. Make sure agent is running in tray.', 'error');
            }
        }
        this.setState({refreshing: false});
    }

    handleInputChange (e) {
        this.setState({searchQuery: e.target.value});
    }

    handlePathChange (e) {
        this.setState({installPath: e.target.value});
    }

    async handleSearch (e) {
        e.preventDefault();
        this.setState({loading: true, boards: [], activeTab: 'searched', fetchError: false});
        try {
            const url = `http://localhost:8605/api/boards/search?q=${encodeURIComponent(this.state.searchQuery)}${this.state.installPath ? `&installPath=${encodeURIComponent(this.state.installPath)}` : ''}`;
            const response = await fetch(url);
            const json = await response.json();
            if (json.success && json.data) {
                const searchResults = Array.isArray(json.data) ? json.data : (json.data.platforms || []);
                const normalizedBoards = searchResults.map(board => {
                    let latestVer = board.latest_version || '';
                    let name = board.id;
                    let architecture = '';
                    if (board.releases && Object.keys(board.releases).length > 0) {
                        const versions = Object.keys(board.releases).sort((a,b) => b.localeCompare(a, undefined, {numeric: true}));
                        const release = board.releases[versions[0]];
                        latestVer = release.version || latestVer;
                        name = release.name || board.id;
                        architecture = release.architecture || '';
                    }
                    return {
                        id: board.id,
                        name: name,
                        author: board.maintainer,
                        website: board.website,
                        architecture: architecture,
                        version: latestVer
                    };
                });
                this.setState({boards: normalizedBoards});
            } else {
                this.showNotification('No boards found.', 'error');
            }
        } catch (error) {
            console.error('Search error:', error);
            if (error instanceof TypeError || (error.message && error.message.includes('Failed to fetch'))) {
                this.setState({fetchError: true});
                this.showNotification('Failed to fetch. Make sure agent is running in tray.', 'error');
            } else {
                this.showNotification('Failed to connect to local board Manager server.', 'error');
            }
        }
        this.setState({loading: false});
    }

    calculateProgress(rawLogs) {
        const lines = rawLogs.split(/[\r\n]+/);
        const packages = {};
        let hasStarted = false;

        const packageRegex = /([a-zA-Z0-9_-]+:[a-zA-Z0-9_.-]+(?:@[a-zA-Z0-9_.-]+)?)/;

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            if (trimmed.includes('Downloading packages...')) {
                hasStarted = true;
            }

            const matchPkg = trimmed.match(packageRegex);
            if (matchPkg) {
                const pkg = matchPkg[1];
                if (!packages[pkg]) {
                    packages[pkg] = {
                        downloadProgress: 0,
                        isInstalling: false,
                        isInstalled: false
                    };
                }

                // Check download percentage
                const pctMatch = trimmed.match(/(\d+(?:\.\d+)?)%/);
                if (pctMatch) {
                    packages[pkg].downloadProgress = parseFloat(pctMatch[1]);
                }

                if (trimmed.includes('already downloaded') || trimmed.includes('downloaded')) {
                    packages[pkg].downloadProgress = 100;
                }

                if (trimmed.includes('already installed')) {
                    packages[pkg].downloadProgress = 100;
                    packages[pkg].isInstalled = true;
                }

                if (trimmed.includes('Installing') || trimmed.includes('Installing platform')) {
                    packages[pkg].isInstalling = true;
                }

                if (trimmed.includes('installed') && !trimmed.includes('already installed') || (trimmed.includes('Platform') && trimmed.includes('installed'))) {
                    packages[pkg].isInstalled = true;
                    packages[pkg].isInstalling = false;
                }
            }
        }

        const keys = Object.keys(packages);
        if (keys.length === 0) {
            return hasStarted ? 5 : 0;
        }

        let totalProgress = 0;
        for (const pkg of keys) {
            const p = packages[pkg];
            const downloadCont = p.downloadProgress * 0.5; // 0 to 50
            const installCont = p.isInstalled ? 50 : (p.isInstalling ? 25 : 0); // 0, 25, or 50
            totalProgress += (downloadCont + installCont);
        }

        return Math.min(99, Math.round(totalProgress / keys.length));
    }

    handleInstall (packageName) {
        this.setState({
            installingId: packageName,
            installProgress: 'Starting install...',
            installPercentage: 0,
            rawLogs: ''
        });
        this.showNotification(`Installing ${packageName}...`, 'info', false);
        
        let url = `http://localhost:8605/api/boards/install-stream?packageName=${encodeURIComponent(packageName)}`;
        if (this.state.installPath) {
            url += `&installPath=${encodeURIComponent(this.state.installPath)}`;
        }

        const eventSource = new EventSource(url);

        eventSource.addEventListener('progress', (e) => {
            try {
                const data = JSON.parse(e.data);
                if (data.output) {
                    const chunk = data.output;
                    this.setState((prevState) => {
                        const nextLogs = prevState.rawLogs + chunk;
                        const nextPercentage = this.calculateProgress(nextLogs);
                        
                        // Parse last meaningful action from chunk
                        const lines = nextLogs.split(/[\r\n]+/);
                        let cleanProgressText = prevState.installProgress;
                        for (let i = lines.length - 1; i >= 0; i--) {
                            const line = lines[i].trim();
                            if (line) {
                                // Strip out progress bar syntax to make it look clean
                                cleanProgressText = line.replace(/\[[=]+>?-*\]/g, '').replace(/\s+/g, ' ').trim();
                                break;
                            }
                        }
                        
                        return {
                            rawLogs: nextLogs,
                            installPercentage: Math.max(prevState.installPercentage, nextPercentage),
                            installProgress: cleanProgressText
                        };
                    });
                }
            } catch (err) {
                // ignore parse error
            }
        });

        eventSource.addEventListener('log', (e) => {
            // Can optionally log stderr here
        });

        eventSource.addEventListener('done', async (e) => {
            eventSource.close();
            try {
                const data = JSON.parse(e.data);
                if (data.success) {
                    // Update state to show 100% complete
                    this.setState({
                        installPercentage: 100,
                        installProgress: 'Installation complete!'
                    });

                    // Optimistic update
                    const nextInstalled = new Set(this.state.installedBoards);
                    nextInstalled.add(packageName);
                    this.setState({ installedBoards: nextInstalled });

                    await this.fetchInstalledboards();
                    
                    // Keep 100% progress visible for 1.5 seconds so user can see it finish
                    await new Promise(resolve => setTimeout(resolve, 1500));
                    
                    this.showNotification(`Successfully installed ${packageName}!`, 'success');
                } else {
                    this.showNotification(`Failed to install ${packageName}.`, 'error');
                }
            } catch (err) {
                this.showNotification(`Failed to install ${packageName}.`, 'error');
            }
            this.setState({installingId: null, installProgress: '', installPercentage: 0, rawLogs: ''});
        });

        eventSource.onerror = (err) => {
            eventSource.close();
            this.showNotification(`Error installing ${packageName}.`, 'error');
            this.setState({installingId: null, installProgress: '', installPercentage: 0, rawLogs: ''});
        };
    }

    async handleUninstall (packageName) {
        this.setState({installingId: packageName});
        this.showNotification(`Uninstalling ${packageName}...`, 'info', false);
        try {
            const response = await fetch('http://localhost:8605/api/boards/uninstall', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    packageName,
                    installPath: this.state.installPath
                })
            });
            const json = await response.json();
            if (json.success) {
                this.showNotification(`Successfully uninstalled ${packageName}!`, 'success');
                // Optimistic update
                const nextInstalled = new Set(this.state.installedBoards);
                nextInstalled.delete(packageName);
                this.setState({ installedBoards: nextInstalled });

                await this.fetchInstalledboards(); // Refresh installed list
            } else {
                this.showNotification(`Failed to uninstall ${packageName}.`, 'error');
            }
        } catch (error) {
            console.error('Uninstall error:', error);
            if (error instanceof TypeError || (error.message && error.message.includes('Failed to fetch'))) {
                this.showNotification('Failed to fetch. Make sure agent is running in tray.', 'error');
            } else {
                this.showNotification(`Error uninstalling ${packageName}.`, 'error');
            }
        }
        this.setState({installingId: null});
    }

    render () {
        return (
            <div className={styles.boardManagerContainer}>
                <div className={styles.arduinoManagerSection}>
                    <div className={styles.managerHeader}>
                        <div className={styles.headerTop}>
                            <div className={styles.titleSection}>
                                <h2>{this.props.intl.formatMessage(messages.boardManagerTitle)}</h2>
                                <p>{this.props.intl.formatMessage(messages.boardManagerDesc)}</p>
                            </div>
                            
                            <form
                                className={styles.searchForm}
                                onSubmit={this.handleSearch}
                            >
                                <div className={styles.searchRow}>
                                    <input
                                        className={styles.searchInput}
                                        placeholder={this.props.intl.formatMessage(messages.searchPlaceholder)}
                                        type="text"
                                        value={this.state.searchQuery}
                                        onChange={this.handleInputChange}
                                    />
                                    <button
                                        className={styles.searchButton}
                                        type="submit"
                                        disabled={this.state.loading}
                                    >
                                        {this.state.loading ? this.props.intl.formatMessage(messages.searching) : this.props.intl.formatMessage(messages.searchBtn)}
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div className={styles.advancedToggleRow}>
                            <span 
                                className={styles.advancedToggle}
                                onClick={this.toggleAdvanced}
                            >
                                {this.state.showAdvanced ? this.props.intl.formatMessage(messages.advancedOptionsHide) : this.props.intl.formatMessage(messages.advancedOptionsShow)}
                            </span>
                            {this.state.showAdvanced && (
                                <div className={styles.advancedOptions}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            className={styles.searchInput}
                                            style={{ flex: 1 }}
                                            placeholder="Optional: Custom Install Path (e.g. C:\Users\name\Documents\Arduino)"
                                            type="text"
                                            value={this.state.installPath}
                                            onChange={this.handlePathChange}
                                            onBlur={this.handlePathBlur}
                                        />
                                        <button 
                                            className={styles.browseButton}
                                            onClick={this.handlePickDirectory}
                                            type="button"
                                        >
                                            Browse...
                                        </button>
                                    </div>
                                    <div className={styles.helpText}>Leave blank to use default Arduino directory. Click outside to apply.</div>
                                </div>
                            )}
                        </div>
                        <div className={styles.tabSwitcher}>
                            <button 
                                className={classNames(styles.switcherTab, { [styles.active]: this.state.activeTab === 'recommended' })}
                                onClick={() => this.switchTab('recommended')}
                            >
                                {this.props.intl.formatMessage(messages.recommended)}
                            </button>
                            <button 
                                className={classNames(styles.switcherTab, { [styles.active]: this.state.activeTab === 'installed' })}
                                onClick={() => this.switchTab('installed')}
                            >
                                {this.props.intl.formatMessage(messages.installed, { count: this.state.installedBoardsData.length })}
                            </button>
                            <button 
                                className={classNames(styles.switcherTab, { [styles.active]: this.state.activeTab === 'searched' })}
                                onClick={() => this.switchTab('searched')}
                            >
                                {this.props.intl.formatMessage(messages.searchResults, { count: this.state.boards.length > 0 ? `(${this.state.boards.length})` : '' })}
                            </button>
                            <button
                                className={styles.refreshButton}
                                onClick={this.handleRefresh}
                                disabled={this.state.refreshing || this.state.loading}
                                title={this.props.intl.formatMessage(messages.refresh)}
                            >
                                {this.state.refreshing || this.state.loading ? this.props.intl.formatMessage(messages.refreshing) : this.props.intl.formatMessage(messages.refresh)}
                            </button>
                        </div>
                    </div>

                    <div className={styles.resultsScrollArea}>
                        {this.state.notificationMessage && (
                            <div className={classNames(styles.toastNotification, styles[this.state.notificationType])}>
                                {this.state.notificationMessage}
                            </div>
                        )}

                        <div className={styles.tabContentContainer}>
                            {this.state.activeTab === 'recommended' && (
                                <div className={styles.recommendedSection}>
                                    <h3 className={styles.recommendedTitle}>Recommended boards</h3>
                                    <div className={styles.recommendedTags}>
                                        {RECOMMENDED_BOARDS.map((lib, idx) => (
                                            <button 
                                                key={idx}
                                                className={styles.recommendedTag}
                                                onClick={() => this.handleRecommendedClick(lib)}
                                            >
                                                {lib}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {this.state.activeTab === 'installed' && (
                                <div className={styles.installedSection}>
                                    <div className={styles.resultsContainer}>
                                        {this.state.installedBoardsData.length === 0 && !this.state.loading && (
                                            <div className={styles.emptyState}>
                                                {this.state.fetchError ? 'Failed to fetch. Make sure agent is running in tray.' : 'No boards installed.'}
                                            </div>
                                        )}
                                        {this.state.installedBoardsData.map(this.renderBoardCard)}
                                    </div>
                                </div>
                            )}
                            
                            {this.state.activeTab === 'searched' && (
                                <div className={styles.searchedSection}>
                                    <div className={styles.resultsContainer}>
                                        {this.state.loading ? (
                                            Array(6).fill(0).map((_, i) => this.renderSkeletonCard(i))
                                        ) : (
                                            <>
                                                {this.state.boards.length === 0 && !this.state.notificationMessage && (
                                                    <div className={styles.emptyState}>
                                                        {this.state.fetchError ? 'Failed to fetch. Make sure agent is running in tray.' : 'No search results.'}
                                                    </div>
                                                )}
                                                {this.state.boards.map(this.renderBoardCard)}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderSkeletonCard(idx) {
        return (
            <div className={classNames(styles.boardCard, styles.skeletonCard)} key={`skeleton-${idx}`}>
                <div className={styles.boardInfo}>
                    <div className={styles.boardHeader}>
                        <div className={styles.skeletonTitle}></div>
                    </div>
                    <div className={styles.skeletonDesc}></div>
                    <div className={styles.skeletonDesc} style={{width: '60%'}}></div>
                    <div className={styles.skeletonMetaRow}></div>
                </div>
                <div className={styles.boardActions}>
                    <div className={styles.skeletonVersion}></div>
                    <div className={styles.skeletonButton}></div>
                </div>
            </div>
        );
    }

    renderBoardCard(board, idx) {
        const isInstalled = this.state.installedBoards.has(board.id);
        return (
            <div
                className={styles.boardCard}
                key={idx}
            >
                <div className={styles.boardInfo}>
                    <div className={styles.boardHeader}>
                        <h3 className={styles.boardTitle}>
                            {board.name}
                            {isInstalled && (
                                <span className={styles.installedBadge}>
                                    ✓ Installed
                                </span>
                            )}
                        </h3>
                    </div>
                    
                    <div className={styles.boardMeta}>
                        {board.author && <span>{board.author.replace(/<.*?>/g, '').trim()}</span>}
                        {board.author && board.website && <span className={styles.metaDot}>•</span>}
                        {board.website && <a href={board.website} target="_blank" rel="noopener noreferrer">Website ↗</a>}
                    </div>
                    
                    <div className={styles.boardDesc}>
                        <strong>Architecture: {board.architecture || 'Unknown'}</strong>
                        <div className={styles.boardParagraph}>ID: {board.id}</div>
                    </div>
                </div>
                
                <div className={styles.boardActions}>
                    {board.version && <span className={styles.boardVersion}>v{board.version}</span>}
                    {this.state.installingId === board.id ? (
                        <div className={styles.progressContainer}>
                            <div className={styles.progressBar}>
                                <div 
                                    className={styles.progressFill} 
                                    style={{ width: `${this.state.installPercentage}%` }}
                                ></div>
                            </div>
                            <span className={styles.progressText}>
                                {this.state.installPercentage > 0 ? `${this.state.installPercentage}% - ` : ''}
                                {this.state.installProgress}
                            </span>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {isInstalled && (
                                <button
                                    className={styles.uninstallButton}
                                    onClick={() => this.handleUninstall(board.id)}
                                >
                                    Uninstall
                                </button>
                            )}
                            <button
                                className={classNames(styles.installButton, {
                                    [styles.isUpdate]: isInstalled
                                })}
                                onClick={() => this.handleInstall(board.id)}
                            >
                                {isInstalled ? 'Update / Reinstall' : 'Install'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

BoardManagerTab.propTypes = {
    intl: intlShape.isRequired
};

export default injectIntl(BoardManagerTab);
